import React from 'react';
import styled from 'styled-components';

interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  label?: string;
  unit?: string;
  disabled?: boolean;
  className?: string;
}

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const SliderLabel = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.darkGray};
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SliderInput = styled.input`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: ${props => props.theme.colors.lightGray};
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => props.theme.colors.blue};
    cursor: pointer;
    border: 2px solid ${props => props.theme.colors.white};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    
    &:hover {
      background: ${props => props.theme.colors.darkBlue};
      transform: scale(1.1);
    }
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => props.theme.colors.blue};
    cursor: pointer;
    border: 2px solid ${props => props.theme.colors.white};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    
    &:hover {
      background: ${props => props.theme.colors.darkBlue};
      transform: scale(1.1);
    }
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.blue};
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    &::-webkit-slider-thumb {
      cursor: not-allowed;
    }
    
    &::-moz-range-thumb {
      cursor: not-allowed;
    }
  }
`;

const ValueDisplay = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.blue};
  font-size: 2rem;
  text-align: center;
  margin-top: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  letter-spacing: -0.5px;
`;

const Slider: React.FC<SliderProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  unit = '',
  disabled = false,
  className
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseInt(e.target.value));
  };

  return (
    <SliderContainer className={className}>
      {label && (
        <SliderLabel>
          {label}
        </SliderLabel>
      )}
      <SliderWrapper>
        <SliderInput
          type="range"
          value={value}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-label={label}
        />
      </SliderWrapper>
      <ValueDisplay>
        {value.toLocaleString('pl-PL')}{unit}
      </ValueDisplay>
    </SliderContainer>
  );
};

export default Slider;
